import type { Dictionary } from './types.js';

// English is the fallback locale (visual design spec section 8.2). Every
// term in `states`/`automationModes`/`attentionReasons`/`actions` is copied
// verbatim from that section's tables, not reworded.
export const en: Dictionary = {
  nav: {
    home: 'Home',
    calendar: 'Calendar',
    campaigns: 'Campaigns',
    projects: 'Projects',
    activity: 'Activity',
    aiConnections: 'AI connections',
    settings: 'Settings',
  },
  states: {
    draft: 'Draft',
    validated: 'Validated',
    needsReview: 'Needs review',
    policyCheck: 'Policy check',
    approved: 'Approved',
    scheduled: 'Scheduled',
    publishing: 'Publishing',
    published: 'Published',
    failed: 'Failed',
    canceled: 'Canceled',
    held: 'Held',
  },
  automationModes: {
    manual: 'Manual',
    guardedAuto: 'Guarded auto',
    autonomous: 'Autonomous',
    automationPaused: 'Automation paused',
  },
  attentionReasons: {
    waitingForApproval: 'Waiting for approval',
    reconnectionRequired: 'Reconnection required',
    validationFailed: 'Validation failed after platform change',
    publicationFailed: 'Publication failed',
    rejectedByPlatform: 'Rejected by platform',
    creditOrRateLimit: 'Credit or rate limit reached',
    mediaInvalid: 'Media missing or invalid',
    dailyCapReached: 'Daily cap reached',
    automationStopped: 'Automation stopped',
    heldOrOverdue: 'Held or overdue',
    campaignPartlyFailed: 'Campaign partly failed',
  },
  actions: {
    approve: 'Approve',
    requestChanges: 'Request changes',
    saveDraft: 'Save draft',
    validate: 'Validate',
    submitForReview: 'Submit for review',
    schedule: 'Schedule',
    reschedule: 'Reschedule',
    cancelSchedule: 'Cancel schedule',
    publishNow: 'Publish now',
    retry: 'Retry',
    reconnect: 'Reconnect',
    revoke: 'Revoke',
    copyToProject: 'Copy to project',
    pauseAutomation: 'Pause automation',
    resumeAutomation: 'Resume automation',
  },
  ui: {
    workspaceAllProjects: 'All projects',
    signedInAs: (email) => `Signed in as ${email}`,
    navNotYetAvailable: 'Not available yet',
    auth: {
      title: 'Sign in',
      emailLabel: 'Email',
      passwordLabel: 'Password',
      submit: 'Sign in',
      submitting: 'Signing in…',
      failure: 'Those details did not match. Check your email and password and try again.',
      sessionLoading: 'Checking your session…',
      signOut: 'Sign out',
    },
    home: {
      title: 'Home',
      subtitle: (count) => (count === 1 ? '1 unresolved item needs you' : `${count} unresolved items need you`),
      createProject: 'Create project',
      needsAttention: 'Needs attention',
      unresolvedCount: (count) => `${count} unresolved`,
      noAttention: 'Nothing needs you right now.',
      projects: 'Projects',
      projectName: 'Project',
      connectionsColumn: 'Connections',
      attentionColumn: 'Attention',
      open: 'Open',
      noProjects: 'No projects yet',
      createProjectPrompt: 'Every campaign lives inside a project. Create one to get started.',
      loading: 'Loading…',
      loadFailed: 'Home could not load. The project list below may still be usable.',
      retry: 'Retry',
      notConnected: 'Not connected',
      automationColumn: 'Automation',
    },
    review: {
      backToHome: 'Back to Needs attention',
      title: 'Review',
      whyReview: 'Why this needs review',
      whyReviewAiBody: 'This revision was written or scheduled over MCP. An MCP grant can create and schedule content, and cannot approve it — approval is a human action in this UI.',
      mcpClientFallback: 'MCP client',
      platformPreview: (platformLabel) => `Platform preview — ${platformLabel}`,
      fieldContent: 'Field content',
      postText: 'Post text',
      readOnlyHint: 'Read-only here. Editing opens the campaign editor and creates a new revision.',
      attachedMedia: (count) => `Attached media — ${count} item${count === 1 ? '' : 's'}`,
      pinTitle: 'Title',
      pinDescription: 'Description',
      pinBoard: 'Board',
      pinLink: 'Destination link',
      pinLinkNone: 'No destination link',
      pinImageAlt: 'Image alt text',
      validation: 'Validation',
      validationPassed: 'Passed',
      validationFailed: 'Failed',
      schedule: 'Schedule',
      notScheduled: 'Not yet scheduled.',
      approveFirst: 'Approve this revision before scheduling it.',
      publishFailed: 'Publication failed',
      publishFailedHint: 'Nothing was posted. Resolve the cause, then edit the content to approve and schedule it again.',
      attemptHistory: 'Attempt history',
      attemptNumber: 'Attempt',
      attemptResult: 'Result',
      attemptReason: 'Reason',
      attemptDetail: 'Detail',
      attemptOk: 'Succeeded',
      attemptFailed: 'Failed',
      recentActivity: 'Recent activity',
      activityWhen: 'When',
      activityOrigin: 'Origin',
      activityAction: 'Action',
      activityResult: 'Result',
      noActivity: 'No activity recorded yet.',
      actions: 'Actions',
      reasonLabel: 'Reason for requesting changes',
      reasonPlaceholder: 'What has to change, and why.',
      reasonHint: 'Returned to the author with the variant and written to activity.',
      requestChangesBlocked: 'Enter a reason first. The variant returns to Draft and the schedule is released.',
      edit: 'Edit',
      editBlocked: 'The campaign editor is not part of this phase.',
      cancelScheduleHint: 'Removes the publication time. The revision and its media are kept.',
      loading: 'Loading…',
      loadFailed: 'This variant could not load.',
      notFound: 'Not found.',
      approveConsequence: (local, utc) => `Approving publishes this post at ${local} / ${utc}, with no further confirmation.`,
      revisionNumber: (n) => `revision ${n}`,
      revisionImmutable: (n) => `revision ${n} — immutable`,
      approveUnavailable: 'Approve is only available while this is Needs review, with a revision that passes validation.',
      requestChangesUnavailable: 'Request changes is not available from the current state.',
      publishedRecord: 'Published record',
      openPublishedPost: 'Open published post',
      publishedCannotBeRecalled: 'This post is live on X. This application cannot recall, edit, or delete a published post.',
    },
    schedulePanel: {
      title: 'Schedule',
      localLabel: 'Local date and time',
      utcLabel: 'Normalized UTC instant',
      timezoneLabel: 'Browser timezone',
      save: 'Save schedule',
      pastError: 'That instant is in the past. Choose a later time.',
      requiredError: 'Choose a date and time.',
      needsApproval: 'Approve this revision before scheduling it.',
      currentlyScheduled: 'Currently scheduled',
    },
    rescheduleConfirm: {
      title: 'Confirm reschedule',
      fromLabel: 'Currently scheduled for',
      toLabel: 'New schedule',
      approvalPreserved: 'This is a schedule-only change. Approval is preserved, and this reschedule will be recorded as an audit event.',
      confirm: 'Confirm reschedule',
      cancel: 'Cancel',
      pastError: 'That new time has already passed. Choose a time later than now.',
      timezoneChanged: (zone) => `Your device's timezone changed to ${zone} while this was open. Review the updated times below before saving.`,
      reviewUpdatedTimes: 'Review updated times',
    },
    project: {
      title: 'Project',
      website: 'Website',
      connectXAccount: 'Connect X account',
      connectPinterestAccount: 'Connect Pinterest account',
      connected: 'Connected',
      notConnected: 'Not connected',
      boards: 'Boards',
      backToHome: 'Back to Home',
      loading: 'Loading…',
      notFound: 'Project not found.',
    },
    counter: {
      weighted: 'weighted',
      codepoints: 'characters',
    },
    createProject: {
      title: 'Create project',
      nameLabel: 'Project name',
      websiteLabel: 'Website (optional)',
      dailyCapLabel: 'Daily publish cap',
      dailyCapHint: 'The most posts this project may publish in a day. Not enforced yet in this phase.',
      submit: 'Create project',
      cancel: 'Cancel',
      nameRequired: 'Enter a project name.',
      dailyCapInvalid: 'Enter a whole number of zero or more.',
      creating: 'Creating…',
      createFailed: 'The project could not be created.',
    },
    projects: {
      title: 'Projects',
      nameColumn: 'Project',
      connectionsColumn: 'Connections',
      automationColumn: 'Automation',
      nextPublishColumn: 'Upcoming publication',
      attentionColumn: 'Attention',
      open: 'Open',
      noUpcoming: 'Nothing scheduled',
      noProjects: 'No projects yet',
      noProjectsBody: 'Every campaign lives inside a project. Create one to get started.',
      loading: 'Loading…',
      loadFailed: 'Projects could not load.',
      retry: 'Retry',
    },
    campaigns: {
      title: 'Campaigns',
      nameColumn: 'Campaign',
      objectiveColumn: 'Objective',
      variantsColumn: 'Variants',
      nextPublishColumn: 'Upcoming publication',
      noObjective: 'No objective set',
      noUpcoming: 'Nothing scheduled',
      noCampaigns: 'No campaigns yet',
      noCampaignsBody: 'Campaigns are created inside a project.',
      loading: 'Loading…',
      loadFailed: 'Campaigns could not load.',
      retry: 'Retry',
      summaryUniform: (count, total, stateLabel) => `${count} of ${total} ${stateLabel}`,
      summaryPart: (count, stateLabel) => `${count} ${stateLabel}`,
      summaryJoin: ', ',
      summaryReviewRequired: (platforms) => `Review required on ${platforms}`,
      summaryNoVariants: 'No variants yet',
      summaryStateLabel: {
        draft: 'draft',
        validated: 'validated',
        needsReview: 'needing review',
        policyCheck: 'in policy check',
        approved: 'approved',
        scheduled: 'scheduled',
        publishing: 'publishing',
        published: 'published',
        failed: 'failed',
        canceled: 'canceled',
        held: 'held',
      },
    },
    activity: {
      title: 'Activity',
      timeColumn: 'Time',
      projectColumn: 'Project',
      originColumn: 'Origin',
      actionColumn: 'Action',
      targetColumn: 'Target',
      resultColumn: 'Outcome',
      noProject: '—',
      loading: 'Loading…',
      loadFailed: 'Activity could not load.',
      retry: 'Retry',
      filterOriginLabel: 'Origin',
      filterOriginAll: 'All',
      noEvents: 'No activity recorded yet.',
      noEventsBody: 'Actions taken in the UI, over MCP, or by the scheduler and publisher will appear here.',
      noMatch: 'No activity matches this filter.',
      noMatchBody: 'Try a different origin, or clear the filter to see everything.',
      resultOk: 'Succeeded',
      resultFailed: 'Failed',
    },
    aiConnections: {
      title: 'AI connections',
      loading: 'Loading…',
      loadFailed: 'AI connections could not load.',
      retry: 'Retry',
      endpointTitle: 'Server endpoint',
      endpointBody: 'This is the address an AI client connects to. It authenticates with a bearer token tied to one authorization grant — never a password or session cookie.',
      copyEndpoint: 'Copy endpoint',
      copied: 'Copied',
      setupTitle: 'Adding this server to an AI client',
      setupBody: 'In the client\'s MCP settings, add a new server pointing at the endpoint above, then supply the bearer token issued when the grant was authorized. A lost token cannot be recovered; authorize again to issue a new one.',
      clientsTitle: 'Authorized clients',
      activeSectionTitle: 'Active',
      revokedSectionTitle: 'Revoked',
      noClientsTitle: 'No client is authorized yet',
      noClientsBody: 'No AI client can reach this workspace until one is authorized. This is expected until the first client connects.',
      noActiveClients: 'No active clients.',
      noRevokedClients: 'No revoked clients.',
      stateActive: 'Active',
      stateRevoked: 'Revoked',
      authorizedAtLabel: 'Authorized',
      lastActivityLabel: 'Last activity',
      lastActivityNever: 'No activity yet',
      readAccessLabel: 'Read scopes',
      writeAccessLabel: 'Write scopes',
      writeAuthorityLabel: 'Write authority',
      noScopes: 'None',
      noWriteAccess: 'None — every write this client makes still waits for human review',
      capabilityApproval: 'Can approve content without further human review',
      capabilityPublish: 'Can publish content immediately',
      capabilityAutomation: 'Can control automation mode and pausing',
      revokeConfirmTitle: 'Revoke this client?',
      revokeConfirmBody: (client) => `${client} will no longer be able to call any tool on this workspace. Revoking does not delete or unpublish anything it already created — this only withdraws its authorization.`,
      revokeConfirmAction: 'Revoke access',
      revokeCancelAction: 'Cancel',
      revoking: 'Revoking…',
      revokeFailed: 'The client could not be revoked. Try again.',
      scopeReferenceTitle: 'Scope reference',
      scopeReferenceBody: 'What each scope permits, independent of which clients currently hold it.',
      scopeDescriptions: {
        projectsRead: 'View project details and brand context.',
        projectsWrite: 'Create and edit projects.',
        campaignsRead: 'View publication status.',
        campaignsWrite: 'Create campaigns and draft or edit post content.',
        mediaWrite: 'Upload and attach media to content.',
        scheduleWrite: 'Schedule or cancel a scheduled post.',
        approvalWrite: 'Approve content for publication without further human review.',
        publishWrite: 'Publish content immediately.',
        automationWrite: 'Control automation mode and pausing.',
        activityRead: 'View the workspace\'s activity log.',
      },
    },
    brandContext: {
      title: 'Brand context',
      intro: 'This context is available to every authorized AI client so it can write and propose content that matches your brand. It is not a place for structured project, campaign, or post-level instructions.',
      backToProject: 'Back to project',
      productSummaryLabel: 'Product or service summary',
      websiteLabel: 'Official website URL',
      targetAudienceLabel: 'Target audience',
      brandVoiceLabel: 'Brand voice and tone',
      aiInstructionsLabel: 'Free-form AI instructions',
      expressionsToAvoidLabel: 'Expressions, promises, or claims to avoid',
      save: 'Save changes',
      saving: 'Saving…',
      saved: 'Saved.',
      saveFailed: 'These changes could not be saved. Try again.',
      loading: 'Loading…',
      loadFailed: 'Brand context could not load.',
      notFound: 'Project not found.',
    },
    calendar: {
      title: 'Calendar',
      viewWeek: 'Week',
      viewMonth: 'Month',
      viewList: 'List',
      previous: 'Previous',
      next: 'Next',
      today: 'Today',
      filterProjectLabel: 'Project',
      filterProjectAll: 'All projects',
      filterPlatformLabel: 'Platform',
      filterPlatformAll: 'All platforms',
      filterStateLabel: 'Status',
      filterStateAll: 'All statuses',
      scheduleStateLabel: {
        pending: 'Pending',
        claimed: 'Claimed',
        done: 'Done',
        canceled: 'Canceled',
        failed: 'Failed',
      },
      timezoneNotice: (tz) => `Times shown in your browser timezone (${tz}).`,
      loading: 'Loading…',
      loadFailed: 'This range could not load. Filters and navigation are still usable.',
      retry: 'Retry',
      noPostsTitle: 'Nothing scheduled',
      noPostsBody: 'No posts are scheduled in this range.',
      filteredEmptyTitle: 'No posts match these filters',
      filteredEmptyBody: 'Posts exist in this range, but the active filters are hiding all of them. Clear a filter to see them.',
      detailTitle: 'Entry',
      detailClose: 'Close',
      detailProjectLabel: 'Project',
      detailCampaignLabel: 'Campaign',
      detailPlatformLabel: 'Platform',
      detailStateLabel: 'State',
      detailScheduleStateLabel: 'Schedule status',
      entryApprovalRequired: 'This post needs approval before it can publish.',
      newTimeLabel: 'New date and time',
      cancelFailed: 'The schedule could not be canceled. Try again.',
      rescheduleFailed: 'This reschedule could not be saved. Try again, or cancel.',
    },
  },
};
